import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import PermissionService from "@ext/security/logic/Permission/components/PermissionService";
import { configureCatalogPermission, configureWorkspacePermission } from "@ext/security/logic/Permission/Permissions";
import isGitSourceType from "@ext/storage/logic/SourceDataProvider/logic/isGitSourceType";
import type SourceData from "@ext/storage/logic/SourceDataProvider/model/SourceData";

const useCloneRights = (catalogName: string) => {
	const { isNext } = usePlatform();
	const workspacePath = WorkspaceService.current()?.path;
	const canConfigureWorkspace = PermissionService.useCheckPermission(configureWorkspacePermission, workspacePath);
	const canConfigureCatalogs = PermissionService.useCheckPermission(configureCatalogPermission, workspacePath);
	const canConfigureThisCatalog = PermissionService.useCheckPermission(
		configureCatalogPermission,
		workspacePath,
		catalogName,
	);

	return {
		checkedByServer: isNext,
		canConfigureWorkspace,
		canConfigureCatalog: canConfigureCatalogs || canConfigureThisCatalog,
	};
};

/**
 * Whether the user may cancel a catalog's clone, drop its failed clone or remove it. On DocPortal the server allows it
 * to those who configure the workspace, or configure this catalog or every catalog of the workspace.
 */
export const useCanManageClone = (catalogName: string): boolean => {
	const { checkedByServer, canConfigureWorkspace, canConfigureCatalog } = useCloneRights(catalogName);
	return !checkedByServer || canConfigureWorkspace || canConfigureCatalog;
};

/** Loading a catalog of the space: on DocPortal it is not registered yet, so the server lets only workspace configurators. */
export const useCanCloneAvailable = (catalogName: string): boolean => {
	const { checkedByServer, canConfigureWorkspace } = useCloneRights(catalogName);
	return !checkedByServer || canConfigureWorkspace;
};

/** Cloning a catalog again: on DocPortal its owner may do it only from its git remote, so only with git storage data. */
export const useCanCloneAgain = (catalogName: string, source: SourceData | null): boolean => {
	const { checkedByServer, canConfigureWorkspace, canConfigureCatalog } = useCloneRights(catalogName);
	return !checkedByServer || canConfigureWorkspace || (canConfigureCatalog && isGitSourceType(source?.sourceType));
};
